import debounce from 'lodash/debounce'
import { makeAutoObservable } from 'mobx'
import { RootStore } from 'stores'
import { Fields, IOption, IWhere } from 'types/common'
import { IFilter } from 'types/common/query'
import {
  getCollection,
  getCollectionsByFilter,
  getCollectionsByFilterWithRelations,
  getCollectionsByGroups,
  upsertCollectionTags
} from 'API/collection'
import { searchGlobal } from 'API/search'
import { getGroups } from 'API/groups'
import { EEsGlobalSearchCollection } from 'constants/elasticSearch'
import { AuthRoleIdEnum } from 'constants/user'
import { ICollection, ICollectionFilter, ICollectionsProcess } from 'interfaces/collection'
import { IGroup, IGroupMemberPermissionId } from 'interfaces/group'
import { IUser } from 'interfaces/user'
import { GlobalSearchDto, ISearchFilterForm } from 'stores/searchStore'
import { IEsGlobalSearchItem } from 'types/eslasticSearch'
import { getValidArray } from 'utils/common'

// Single source of truth for "valid (visible) process inside a collection".
// Keep server-side `where` and client-side post-filter in sync — divergence
// between the two is exactly what caused the Quick View vs Detail count
// mismatch (e.g. Altec AIR collection 594 showing 14 vs 22).
const VALID_PROCESS_WHERE = {
  archivedAt: { inq: [undefined, null] },
  isPublished: { neq: false }
}

const isValidCollectionsProcess = (collectionProcess?: ICollectionsProcess): boolean =>
  !!collectionProcess?.process &&
  !collectionProcess.process.archivedAt &&
  collectionProcess.process.isPublished !== false

class CollectionStore {
  rootStore: RootStore

  isSearching: boolean | null = null
  isLoading = false
  isEditing = false
  isManageMode = false
  isManagePermission = false
  isOpenDiscardModal = false

  collections: ICollection[] = []
  selectedCollectionId: number | null = null
  totalCollections: number = 0
  size: number = 8
  from: number = 0

  overviewCollection: ICollection | null = null
  collection: ICollection | null = null

  groupFilterOptions: IGroup[] = []
  lastFilter: ICollectionFilter | null = null

  collectionProcessSearchResults: IEsGlobalSearchItem[] = []
  collectionProcessTotalResults: number = 0
  isSearchingCollectionProcesses: boolean | null = null
  collectionProcessSearchText: string = ''
  collectionProcessSearchFilter: ISearchFilterForm = {
    documentTypes: [],
    tags: [],
    creators: []
  }
  collectionProcessIds: number[] = []
  collectionProcessCacheFilter: IFilter<GlobalSearchDto> = {}
  collectionProcessPage: number = 1
  collectionProcessPageSize: number = 10

  debouncedSearchCollectionProcesses: () => void

  constructor(rootStore: RootStore) {
    makeAutoObservable(this)

    this.rootStore = rootStore
    this.debouncedSearchCollectionProcesses = debounce(() => this.searchCollectionProcesses(), 700)
  }

  public changePage(page: number, fetchFunction: () => Promise<void>): void {
    this.from = (page - 1) * this.size
    fetchFunction()
  }

  public changeSize = (pageIndex: number = 8): void => {
    this.size = Number(pageIndex)
  }

  public async getOverviewCollection(collectionId: number): Promise<ICollection> {
    this.selectedCollectionId = collectionId
    // Mirror getCollectDetail: clear stale data so the modal does not flash
    // the previous collection's processes while the new fetch is in flight.
    this.overviewCollection = null
    const collection: ICollection = await getCollection(collectionId, {
      include: [
        {
          relation: 'collectionsProcesses',
          scope: {
            fields: ['processId', 'collectionId'],
            include: [
              {
                relation: 'process',
                scope: {
                  where: VALID_PROCESS_WHERE,
                  fields: ['id', 'image', 'name', 'procedureIcon', 'documentTypeId', 'archivedAt', 'isPublished'],
                  include: [
                    {
                      relation: 'steps',
                      scope: {
                        fields: ['id', 'processId'],
                        include: [
                          {
                            relation: 'media',
                            scope: {
                              fields: ['id', 'image', 'stepId', 'mediaType', 'video'],
                              where: {
                                mediaType: { inq: ['image', 'video'] }
                              },
                              order: ['position ASC']
                            }
                          }
                        ],
                        order: ['position ASC']
                      }
                    },
                    {
                      relation: 'documentType',
                      scope: {
                        include: [
                          {
                            relation: 'iconBuilder'
                          }
                        ]
                      }
                    }
                  ]
                }
              }
            ],
            order: ['position ASC']
          }
        },
        {
          relation: 'groups'
        },
        {
          relation: 'tags'
        }
      ]
    })
    const collectionsProcesses: ICollectionsProcess[] = getValidArray(collection?.collectionsProcesses).filter(
      isValidCollectionsProcess
    )

    this.overviewCollection = { ...collection, collectionsProcesses }

    return collection
  }

  public async getCollectDetail(collectionId: number): Promise<ICollection> {
    this.selectedCollectionId = collectionId
    this.collection = null
    const collection: ICollection = await getCollection(collectionId, {
      include: [
        {
          relation: 'collectionsProcesses',
          scope: {
            fields: ['processId', 'collectionId'],
            include: [
              {
                relation: 'process',
                scope: {
                  where: VALID_PROCESS_WHERE,
                  fields: [
                    'id',
                    'image',
                    'name',
                    'procedureIcon',
                    'documentTypeId',
                    'archivedAt',
                    'createdBy',
                    'isPublished',
                    'organizationId',
                    'mainMedia'
                  ],
                  include: [
                    {
                      relation: 'steps',
                      scope: {
                        fields: ['id', 'processId', 'name', 'position'],
                        include: [
                          {
                            relation: 'media',
                            scope: {
                              fields: ['id', 'image', 'stepId', 'mediaType', 'video'],
                              where: {
                                mediaType: { inq: ['image', 'video'] }
                              },
                              order: ['position ASC']
                            }
                          }
                        ],
                        order: ['position ASC']
                      }
                    },
                    {
                      relation: 'documentType',
                      scope: {
                        include: [
                          {
                            relation: 'iconBuilder'
                          }
                        ]
                      }
                    },
                    {
                      relation: 'tags'
                    },
                    {
                      relation: 'creator',
                      scope: {
                        fields: ['id', 'firstName', 'lastName', 'image']
                      }
                    }
                  ]
                }
              }
            ],
            order: ['position ASC']
          }
        },
        {
          relation: 'userCollections'
        },
        {
          relation: 'groups'
        },
        {
          relation: 'tags'
        }
      ]
    })

    const groupIdsOfCollection = getValidArray(collection?.groups).map(group => group?.id)
    const editorGroupIdsOfUser = getValidArray(this.rootStore.userStore.currentUserGroupMembers)
      .filter(groupMember => groupMember?.permissionId !== IGroupMemberPermissionId.VIEWER)
      .map(groupMember => groupMember?.groupId)

    const isManagePermission = groupIdsOfCollection?.some(groupId => editorGroupIdsOfUser?.includes(groupId ?? 0))

    this.isManagePermission = isManagePermission

    this.collection = {
      ...collection,
      collectionsProcesses: getValidArray(collection?.collectionsProcesses).filter(isValidCollectionsProcess)
    }
    return collection
  }

  public async updateCollectionTags(collectionId: number, tagIds: number[]): Promise<void> {
    await upsertCollectionTags(collectionId, tagIds)
    if (collectionId === this.collection?.id) {
      this.collection = {
        ...this.collection,
        tagIds,
        tags: getValidArray(this.collection?.tags).filter(tag => tagIds.includes(tag.id ?? 0))
      }
      const missingTagIds = tagIds.filter(tagId => !getValidArray(this.collection?.tags).some(tag => tag.id === tagId))
      if (missingTagIds.length > 0) {
        const refreshed = await this.getCollectDetail(collectionId)
        this.collection = refreshed
      }
    }
  }

  public async fetchCollectionsByFilter(filter: ICollectionFilter, useLastFilter: boolean = false): Promise<void> {
    this.isLoading = true
    const apiFilter = useLastFilter && this.lastFilter ? { ...this.lastFilter, skip: 0 } : filter
    const response = await getCollectionsByFilter(apiFilter)
    this.collections = response.paginatedResults ?? []
    this.totalCollections = response?.totalResults ?? 0
    if (!useLastFilter) {
      this.lastFilter = filter
    }
    this.isLoading = false
  }

  public async fetchCollectionsByFilterWithRelations(
    filter: ICollectionFilter,
    useLastFilter: boolean = false
  ): Promise<void> {
    this.isLoading = true
    const apiFilter = useLastFilter && this.lastFilter ? { ...this.lastFilter, skip: 0 } : filter
    const response = await getCollectionsByFilterWithRelations(apiFilter)
    this.collections = response.paginatedResults ?? []
    this.totalCollections = response?.totalResults ?? 0
    if (!useLastFilter) {
      this.lastFilter = filter
    }
    this.isLoading = false
  }

  public async fetchCollectionsByGroup(filter: {
    where: IWhere<ICollection | any>
    groups?: number[] | string[]
    limit?: number
    offset?: number
    fields?: Fields<ICollection>
    order?: string[]
  }): Promise<{ result: ICollection[]; totalCount: number }> {
    this.isSearching = true
    const response = await getCollectionsByGroups(filter)
    this.collections = response.result
    this.totalCollections = response.totalCount
    this.isSearching = false
    return response
  }

  public async fetchGroupOptions(userDetail: IUser): Promise<IOption<number>[]> {
    const isBasicUser = userDetail?.authRoleId === AuthRoleIdEnum.BASIC_USER
    const groupOptions: IOption<number>[] = []
    if (isBasicUser) {
      const currentUserGroupMembers = this?.rootStore?.userStore?.currentUserGroupMembers ?? []
      const groupIdsUserIsMember = currentUserGroupMembers.map(groupMember => groupMember.groupId)
      const basicUserQuery = {
        where: {
          id: {
            inq: groupIdsUserIsMember
          }
        },
        fields: ['id', 'name']
      }
      const groups = await getGroups(basicUserQuery)
      this.groupFilterOptions = groups
      groupOptions.push(
        ...[
          ...groups.map(group => ({
            label: group?.name ?? '',
            value: Number(group?.id)
          }))
        ]
      )
    } else {
      const organizationId = userDetail?.organizationId ?? 0
      const adminQuery = {
        where: {
          organizationId
        },
        fields: ['id', 'name']
      }
      const groups = await getGroups(adminQuery)
      this.groupFilterOptions = groups
      groupOptions.push(
        ...[
          ...groups.map(group => ({
            label: group?.name ?? '',
            value: Number(group?.id)
          }))
        ]
      )
    }
    return groupOptions
  }

  public async fetchGroupsWithEditorPermissions(userDetail: IUser): Promise<IOption<number>[]> {
    const isBasicUser = userDetail?.authRoleId === AuthRoleIdEnum.BASIC_USER
    const groupOptions: IOption<number>[] = []

    if (isBasicUser) {
      // For basic users, only get groups where they have editor permissions
      const currentUserGroupMembers = this?.rootStore?.userStore?.currentUserGroupMembers ?? []
      const editorGroupIds = currentUserGroupMembers
        .filter(groupMember => groupMember?.permissionId === IGroupMemberPermissionId.EDITOR)
        .map(groupMember => groupMember.groupId)

      if (editorGroupIds.length > 0) {
        const basicUserQuery = {
          where: {
            id: {
              inq: editorGroupIds
            }
          },
          fields: ['id', 'name']
        }
        const groups = await getGroups(basicUserQuery)
        groupOptions.push(
          ...groups.map(group => ({
            label: group?.name ?? '',
            value: Number(group?.id)
          }))
        )
      }
    } else {
      // For admins, get all groups in organization
      const organizationId = userDetail?.organizationId ?? 0
      const adminQuery = {
        where: {
          organizationId
        },
        fields: ['id', 'name']
      }
      const groups = await getGroups(adminQuery)
      groupOptions.push(
        ...groups.map(group => ({
          label: group?.name ?? '',
          value: Number(group?.id)
        }))
      )
    }
    return groupOptions
  }

  public async fetchCollectionsWithEditorPermissions(userDetail: IUser): Promise<IOption<number>[]> {
    const isBasicUser = userDetail?.authRoleId === AuthRoleIdEnum.BASIC_USER
    const organizationId = userDetail?.organizationId ?? 0

    if (isBasicUser) {
      // For basic users, get collections where they have editor permissions through groups
      const currentUserGroupMembers = this?.rootStore?.userStore?.currentUserGroupMembers ?? []
      const editorGroupIds = currentUserGroupMembers
        .filter(groupMember => groupMember?.permissionId === IGroupMemberPermissionId.EDITOR)
        .map(groupMember => groupMember.groupId)

      if (editorGroupIds.length === 0) {
        return []
      }

      const filter = {
        where: {
          organizationId,
          $or: [{ archivedAt: { $exists: false } }, { archivedAt: { $eq: null } }]
        },
        include: [
          {
            relation: 'groups',
            scope: {
              where: {
                id: { inq: editorGroupIds }
              }
            }
          }
        ],
        fields: { id: true, name: true }
      }

      const response = await getCollectionsByGroups(filter)
      const collections = response.result

      return collections.map(collection => ({
        label: collection?.name ?? '',
        value: Number(collection?.id)
      }))
    }

    // For admins, get all collections in organization
    const filter = {
      where: {
        organizationId,
        $or: [{ archivedAt: { $exists: false } }, { archivedAt: { $eq: null } }]
      },
      fields: { id: true, name: true }
    }

    const response = await getCollectionsByGroups(filter)
    return response.result.map(collection => ({
      label: collection?.name ?? '',
      value: Number(collection?.id)
    }))
  }

  public setCollections(collections: ICollection[], count: number) {
    this.collections = collections
    this.totalCollections = count
  }

  public setIsEditing = (isEditing: boolean): void => {
    this.isEditing = isEditing
  }

  public setIsOpenDiscardModal = (isOpenDiscardModal: boolean): void => {
    this.isOpenDiscardModal = isOpenDiscardModal
  }

  public setManageMode = (isManageMode: boolean, isCollectionDetailPage?: boolean): void => {
    if (isCollectionDetailPage && this.isManageMode && this.isEditing) {
      this.setIsOpenDiscardModal(true)
      return
    }
    this.isManageMode = isManageMode
  }

  public setIsManagePermission = (isManagePermission: boolean): void => {
    this.isManagePermission = isManagePermission
  }

  public resetCollection = (): void => {
    this.collection = null
    this.overviewCollection = null
  }

  public changeCollectionProcessIds(processIds: number[]): void {
    this.collectionProcessIds = processIds
  }

  public changeCollectionProcessSearchText(searchText: string): void {
    this.collectionProcessSearchText = searchText
    this.collectionProcessPage = 1
    this.debouncedSearchCollectionProcesses()
  }

  public changeCollectionProcessSearchFilter(filter: ISearchFilterForm): void {
    this.collectionProcessSearchFilter = filter
    this.collectionProcessPage = 1
  }

  public changeCollectionProcessPage(page: number): void {
    this.collectionProcessPage = page
    this.searchCollectionProcesses()
  }

  public changeCollectionProcessPageSize(size: number): void {
    if (this.collectionProcessPageSize === Number(size)) return
    this.collectionProcessPageSize = Number(size)
    this.collectionProcessPage = 1
    this.searchCollectionProcesses()
  }

  // Silent variants — update pagination without triggering an ES search.
  // Used when the consumer paginates LoopBack data client-side and ES is
  // not the source of truth (e.g. Collection Detail with no search/filter).
  public setCollectionProcessPage(page: number): void {
    this.collectionProcessPage = page
  }

  public setCollectionProcessPageSize(size: number): void {
    if (this.collectionProcessPageSize === Number(size)) return
    this.collectionProcessPageSize = Number(size)
    this.collectionProcessPage = 1
  }

  public async searchCollectionProcesses(): Promise<void> {
    if (this.collectionProcessIds.length === 0) {
      return
    }

    try {
      this.isSearchingCollectionProcesses = true

      const filter: IFilter<GlobalSearchDto> = {
        where: {
          searchText: this.collectionProcessSearchText ?? '',
          isPublished: true,
          collectionName: EEsGlobalSearchCollection.PROCESS,
          processIds: this.collectionProcessIds
        },
        skip: (this.collectionProcessPage - 1) * this.collectionProcessPageSize,
        limit: this.collectionProcessPageSize
      }

      if (getValidArray(this.collectionProcessSearchFilter.documentTypes).length > 0) {
        filter.where!.documentTypeIds = getValidArray(this.collectionProcessSearchFilter.documentTypes).map(dt =>
          Number(dt.value)
        )
      }

      if (getValidArray(this.collectionProcessSearchFilter.tags).length > 0) {
        filter.where!.tagIds = getValidArray(this.collectionProcessSearchFilter.tags).map(tag => Number(tag.value))
      }

      if (getValidArray(this.collectionProcessSearchFilter.creators).length > 0) {
        filter.where!.creatorIds = getValidArray(this.collectionProcessSearchFilter.creators).map(c => Number(c.value))
      }

      if (
        JSON.stringify(this.collectionProcessCacheFilter) === JSON.stringify(filter) &&
        this.collectionProcessSearchResults.length > 0
      ) {
        return
      }
      this.collectionProcessCacheFilter = filter

      const result = await searchGlobal(filter)
      this.collectionProcessSearchResults = result.data
      this.collectionProcessTotalResults = result.total
    } catch (error: unknown) {
      console.error('CollectionStore ~ searchCollectionProcesses ~ error', error instanceof Error ? error.message : error)
    } finally {
      this.isSearchingCollectionProcesses = false
    }
  }

  public clearCollectionProcessSearch(): void {
    this.collectionProcessSearchResults = []
    this.collectionProcessTotalResults = 0
    this.isSearchingCollectionProcesses = null
    this.collectionProcessSearchText = ''
    this.collectionProcessSearchFilter = {
      documentTypes: [],
      tags: [],
      creators: []
    }
    this.collectionProcessIds = []
    this.collectionProcessCacheFilter = {}
    this.collectionProcessPage = 1
  }
}

export default CollectionStore
